import { pushService } from '../services/pushService';
import { STORAGE_KEYS } from '../utils/constants';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

function bufferToBase64Url(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function isWebPushSupported() {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/** Chrome/Firefox/Edge on iOS — WebKit shell; Home Screen push is most reliable via Safari. */
export function isIosNonSafariBrowser() {
  if (!isIosDevice()) return false;
  const ua = navigator.userAgent || '';
  return /CriOS|FxiOS|EdgiOS|OPiOS|OPT\//i.test(ua);
}

/** True when iOS cannot reliably receive OS push (wrong browser or not installed PWA). */
export function isIosPushBlocked() {
  if (!isIosDevice()) return false;
  return isIosNonSafariBrowser() || !isStandalonePwa();
}

/** Why push is unavailable — for clearer UI on HTTP deploys. */
export function getWebPushUnsupportedReason() {
  if (typeof window === 'undefined') {
    return 'Web Push is only available in a browser.';
  }
  if (!window.isSecureContext) {
    return 'Web Push requires HTTPS. This site is on HTTP, so the browser blocks notifications. Serve the app over HTTPS (or use localhost for local testing).';
  }
  if (isIosDevice() && isIosNonSafariBrowser()) {
    return 'On iPhone/iPad, system alerts work best from Safari: open this site in Safari → Share → Add to Home Screen → open the home-screen app → enable alerts. Chrome/Firefox on iOS often cannot deliver OS push reliably.';
  }
  if (isIosDevice() && !isStandalonePwa()) {
    return 'On iPhone/iPad, open this site in Safari, tap Share → Add to Home Screen, then open Prasaar from the home screen icon and enable alerts. Push does not work from a normal Safari tab.';
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'This browser does not support Web Push. Use Chrome or Edge on Android/desktop, and install the PWA for OS notifications.';
  }
  if (!('Notification' in window)) {
    return 'Notifications API is not available in this browser.';
  }
  return null;
}

export function getIosPushGuidance() {
  if (!isIosDevice()) return null;
  if (isIosNonSafariBrowser()) {
    return 'iPhone tip: use Safari (not Chrome) → Share → Add to Home Screen → open that icon → enable alerts. Android Chrome PWAs work; iOS Chrome usually does not.';
  }
  if (!isStandalonePwa()) {
    return 'iPhone tip: Share → Add to Home Screen, then open Prasaar from the home screen before enabling alerts.';
  }
  return 'If alerts still fail after enabling, ask an admin to confirm Render VAPID_SUBJECT is mailto:alerts@prasaar.app (not .local), then disable/enable alerts here once.';
}

async function waitForServiceWorker(timeoutMs = 8000) {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported in this browser.');
  }

  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active || existing?.waiting || existing?.installing) {
    return navigator.serviceWorker.ready;
  }

  // Try registering the dev/prod SW if nothing is registered yet
  const swUrl =
    process.env.NODE_ENV === 'production'
      ? `${process.env.PUBLIC_URL || ''}/service-worker.js`
      : `${process.env.PUBLIC_URL || ''}/sw.js`;

  try {
    await navigator.serviceWorker.register(swUrl);
  } catch (err) {
    throw new Error(
      `Could not register service worker (${swUrl}). ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }

  const ready = navigator.serviceWorker.ready;
  const timeout = new Promise((_, reject) => {
    window.setTimeout(() => {
      reject(
        new Error(
          'Service worker did not become ready. Reload the page and try again.',
        ),
      );
    }, timeoutMs);
  });

  return Promise.race([ready, timeout]);
}

export async function getPushStatus() {
  if (!isWebPushSupported()) return 'unsupported';
  const permission = Notification.permission;
  if (permission === 'denied') return 'denied';
  if (permission === 'default') return 'default';

  try {
    const reg = await waitForServiceWorker(5000);
    const sub = await reg.pushManager.getSubscription();
    if (sub) return 'subscribed';
    return 'granted';
  } catch {
    return 'granted';
  }
}

export async function enableWebPush({ forceRefresh = true } = {}) {
  if (!isWebPushSupported()) return 'unsupported';
  if (isIosDevice() && isIosNonSafariBrowser()) return 'unsupported';
  if (isIosDevice() && !isStandalonePwa()) return 'ios-needs-install';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return permission === 'denied' ? 'denied' : 'default';
  }

  const { publicKey } = await pushService.getVapidPublicKey();
  if (!publicKey) return 'missing-vapid';

  const registration = await waitForServiceWorker();

  let subscription = await registration.pushManager.getSubscription();
  if (forceRefresh && subscription) {
    const oldEndpoint = subscription.endpoint;
    try {
      if (oldEndpoint) {
        await pushService.unregisterDevice({ token: oldEndpoint });
      }
    } catch (err) {
      console.warn('PRASAAR_PUSH unregister old device skipped', err);
    }
    try {
      await subscription.unsubscribe();
      console.log('PRASAAR_PUSH unsubscribed old endpoint');
      subscription = null;
    } catch (err) {
      console.warn('PRASAAR_PUSH unsubscribe skipped', err);
    }
  }

  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      throw new Error(
        `Push subscribe failed: ${detail}. Open this HTTPS site in Chrome or Edge — not an in-app or Cursor preview.`,
      );
    }
  }

  const json = subscription.toJSON();
  const endpoint = json.endpoint || subscription.endpoint;
  const p256dh =
    json.keys?.p256dh || bufferToBase64Url(subscription.getKey('p256dh'));
  const auth =
    json.keys?.auth || bufferToBase64Url(subscription.getKey('auth'));

  const registered = await pushService.registerDevice({
    token: endpoint,
    platform: 'web',
    keys: { p256dh, auth },
  });

  localStorage.setItem(STORAGE_KEYS.PUSH_ENABLED, '1');
  try {
    console.log(
      'PRASAAR_PUSH device registered',
      JSON.stringify({
        id: registered?.id ?? null,
        userId: registered?.userId ?? null,
        host: endpoint ? new URL(endpoint).host : null,
        endpointTail: endpoint ? endpoint.slice(-24) : null,
      }),
    );
  } catch {
    console.log('PRASAAR_PUSH device registered');
  }
  return 'subscribed';
}

let ensureWebPushInFlight = null;

export async function ensureWebPushRegistered() {
  if (!isWebPushSupported()) return;
  if (Notification.permission !== 'granted') return;
  if (localStorage.getItem(STORAGE_KEYS.PUSH_ENABLED) !== '1') return;

  if (ensureWebPushInFlight) return ensureWebPushInFlight;

  ensureWebPushInFlight = (async () => {
    try {
      // Do not force-refresh on every page load — only sync existing subscription.
      await enableWebPush({ forceRefresh: false });
    } catch (err) {
      console.warn('Web push registration skipped:', err);
    } finally {
      ensureWebPushInFlight = null;
    }
  })();

  return ensureWebPushInFlight;
}

/** Drop this browser's push device before the session token is cleared. */
export async function disableWebPushOnLogout() {
  try {
    let token;
    if (isWebPushSupported()) {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager?.getSubscription();
      token = subscription?.endpoint;
      try {
        if (token) {
          await pushService.unregisterDevice({ token });
        }
      } catch (err) {
        console.warn('PRASAAR_PUSH logout unregister skipped', err);
      }
      if (subscription) {
        await subscription.unsubscribe().catch(() => {});
      }
    }
  } catch (err) {
    console.warn('PRASAAR_PUSH logout cleanup skipped', err);
  } finally {
    try {
      localStorage.removeItem(STORAGE_KEYS.PUSH_ENABLED);
    } catch {
      /* ignore */
    }
  }
}

export function markPushPromptSeen() {
  localStorage.setItem(STORAGE_KEYS.PUSH_PROMPT_SEEN, '1');
}

export function shouldShowPushPrompt() {
  if (!isWebPushSupported()) return false;
  if (Notification.permission !== 'default') return false;
  return localStorage.getItem(STORAGE_KEYS.PUSH_PROMPT_SEEN) !== '1';
}

/** Call after a successful login so the feed can request permission once. */
export function markPushPromptPendingAfterLogin() {
  try {
    sessionStorage.setItem(STORAGE_KEYS.PUSH_PROMPT_AFTER_LOGIN, '1');
  } catch {
    /* private mode / blocked storage */
  }
}

/**
 * Start the OS notification dialog from a user gesture (Sign in click).
 * Must be called synchronously from the click/submit handler — iOS PWA
 * drops the gesture if we wait for the login API first.
 */
export function startLoginPushPermission() {
  if (!isWebPushSupported() || typeof Notification === 'undefined') {
    return Promise.resolve('unsupported');
  }
  if (isIosDevice() && (isIosNonSafariBrowser() || !isStandalonePwa())) {
    return Promise.resolve('unsupported');
  }
  if (Notification.permission !== 'default') {
    return Promise.resolve(Notification.permission);
  }
  try {
    return Notification.requestPermission();
  } catch (err) {
    console.warn('Login push permission skipped:', err);
    return Promise.resolve(Notification.permission);
  }
}

/** After login succeeds, register the device if the OS prompt was granted. */
export async function finishLoginPushPermission(permissionPromise) {
  let permission = 'default';
  try {
    permission = (await permissionPromise) || Notification.permission;
  } catch (err) {
    console.warn('Login push permission failed:', err);
    permission =
      typeof Notification !== 'undefined' ? Notification.permission : 'default';
  }

  markPushPromptSeen();

  if (permission === 'granted') {
    try {
      await enableWebPush({ forceRefresh: true });
    } catch (err) {
      console.warn('Login push register skipped:', err);
    }
    return 'subscribed';
  }

  if (permission === 'denied' || permission === 'unsupported') {
    return permission;
  }

  return 'default';
}

export function clearPushPromptPendingAfterLogin() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.PUSH_PROMPT_AFTER_LOGIN);
  } catch {
    /* ignore */
  }
}

function consumePushPromptPendingAfterLogin() {
  try {
    if (sessionStorage.getItem(STORAGE_KEYS.PUSH_PROMPT_AFTER_LOGIN) !== '1') {
      return false;
    }
    sessionStorage.removeItem(STORAGE_KEYS.PUSH_PROMPT_AFTER_LOGIN);
    return true;
  } catch {
    return false;
  }
}

/**
 * On the first feed visit after login, request notification permission
 * (browser + installed PWA) and register the device when granted.
 */
export async function promptWebPushFromFeedIfNeeded() {
  if (!consumePushPromptPendingAfterLogin()) return null;
  if (!isWebPushSupported()) return 'unsupported';
  if (isIosDevice() && isIosNonSafariBrowser()) return 'unsupported';
  if (isIosDevice() && !isStandalonePwa()) return 'unsupported';

  markPushPromptSeen();

  try {
    if (Notification.permission === 'denied') return 'denied';
    if (Notification.permission === 'granted') {
      await enableWebPush({ forceRefresh: false });
      return 'subscribed';
    }
    return await enableWebPush({ forceRefresh: true });
  } catch (err) {
    console.warn('Feed push prompt skipped:', err);
    return null;
  }
}
